import AMapLoader from '@amap/amap-jsapi-loader'
import { App, reactive } from 'vue'
import { AMapConfig } from '/@/constants/index'

import Map from 'ol/Map'
import View from 'ol/View'
import { fromLonLat, toLonLat } from 'ol/proj'
import GeoTIFF from 'ol/source/GeoTIFF.js'
import TileLayer from 'ol/layer/WebGLTile'
import VectorTileLayer from 'ol/layer/VectorTile'
import VectorTileSource from 'ol/source/VectorTile'
import ImageTile from 'ol/source/ImageTile'
import XYZ from 'ol/source/XYZ'
import VectorLayer from 'ol/layer/Vector'
import VectorSource from 'ol/source/Vector'
import MVT from 'ol/format/MVT'
import Point from 'ol/geom/Point'
import { Icon, Circle, Fill, Stroke, Style, Text } from 'ol/style'
import { Draw } from 'ol/interaction'
import GeoJSON from 'ol/format/GeoJSON'
import { vectorTileStyle } from '/@/hooks/use-g-map-style'

import * as Cesium from 'cesium'
import 'cesium/Build/Cesium/Widgets/widgets.css'
import OLCesium from 'olcs'
import { ERouterName } from '../types/enums'
import Google from 'ol/source/Google'
import googleMapLogo from '/@/assets/Google_Maps_Attribution_Assets/GoogleMaps_Logo_Gray/GoogleMaps_Logo_Gray.svg'
import { defaults as defaultControls } from 'ol/control/defaults.js'
import Control from 'ol/control/Control'
import Attribution from 'ol/control/Attribution'

export function useGMapManage () {
  const state = reactive({
    aMap: null, // Map类
    map: null, // 地图对象
    mouseTool: null,
    flightRouteArrowLayer: null,
    flightRouteBoundaryLayer: Draw,
    contourLineLayer10: null,
    // contourLineLayer30: null
  })

  async function initMap (container: string, app:App) {
    // const mapStyle = await import('/@/assets/map/style.json')
    // AMapLoader.load({
    //   ...AMapConfig
    // }).then((AMap) => {
    //   state.aMap = AMap
    //   state.map = new AMap.Map(container, {
    //     center: [113.943225499, 22.577673716],
    //     zoom: 20
    //   })
    //   state.mouseTool = new AMap.MouseTool(state.map)

    //   // 挂在到全局
    //   app.config.globalProperties.$aMap = state.aMap
    //   app.config.globalProperties.$map = state.map
    //   app.config.globalProperties.$mouseTool = state.mouseTool
    // }).catch(e => {
    //   console.log(e)
    // })
    const waterLayer = new VectorTileLayer({
      source: new VectorTileSource({
        format: new MVT(),
        url: AMapConfig.url + '/tiles/simplified-water/{z}/{x}/{y}.pbf',
        minZoom: 0,
        maxZoom: 5,
      }),
      style: vectorTileStyle
    })

    const southKoreaLayer = new VectorTileLayer({
      source: new VectorTileSource({
        format: new MVT(),
        url: AMapConfig.url + '/tiles/south-korea-osm/{z}/{x}/{y}.pbf',
        minZoom: 0,
        maxZoom: 9,
      }),
      style: vectorTileStyle
    })

    const landLayer = new VectorTileLayer({
      source: new VectorTileSource({
        format: new MVT(),
        url: AMapConfig.url + '/tiles/simplified-land/{z}/{x}/{y}.pbf',
        minZoom: 0,
        maxZoom: 5,
      }),
      style: vectorTileStyle
    })

    const asiaCementBaseLayer =
      new TileLayer({
        id: 'asiaCementBaseLayer',
        source: new GeoTIFF({
          sources: [
            {
              url: AMapConfig.url + AMapConfig.path + '/orthomosaic_cog_3857.tif',
            }
          ],
          transition: 0,
          // projection: 'EPSG:3857'
        }),
        cacheSize: 256,
        maxZoom: 19,
      })

    // tiles better for greateer zoom > 19
    const asiaCementBaseXyzLayer =
      new TileLayer({
        id: 'asiaCementBaseXyzLayer',
        source: new XYZ({
          // url: AMapConfig.url + '/tiles-raster/asia-cement/current/tiles/{z}/{x}/{-y}.png',
          url: AMapConfig.url + AMapConfig.path + '/tiles/{z}/{x}/{y}.png',
          tileSize: 256,
          transition: 0,
        }),
        preload: 9, // Prefetch neighbor tiles for smooth panning
        maxZoom: 22,
        minZoom: 18,
      })

    const googleMapKey = AMapConfig.gmapKey
    const googleMapLayer =
      new TileLayer({
        id: 'googleMapLayer',
        source: new Google({
          key: googleMapKey,
          mapType: 'satellite', // 'satellite' | 'roadmap' | 'hybrid' | 'terrain'
          highDpi: true, // Better quality on retina screens
          scale: 'scaleFactor2x', // Optional extra scaling
          region: 'KR',
          attributionsCollapsible: true,
        }),
        visible: true,
      })
    class GoogleLogoControl extends Control {
      constructor () {
        const element = document.createElement('img')
        element.style.pointerEvents = 'none'
        element.style.position = 'absolute'
        element.style.bottom = '5px'
        element.style.left = '5px'
        element.style.display = 'block'
        element.style.opacity = '1'
        element.src = googleMapLogo
        super({
          element: element,
        })
        this.name = 'GoogleLogoControl'
        this.element = element
      }

      show () {
        this.element.style.display = 'block'
        this.element.style.opacity = '1'
      }

      hide () {
        this.element.style.opacity = '0'
        setTimeout(() => {
          this.element.style.display = 'none'
        }, 300)
      }
    }
    const white = [255, 255, 255, 1]
    const blue = [0, 128, 255, 1]
    const width = 3
    const flightRouteBoundaryLayer = new VectorLayer({
      source: new VectorSource({ wrapX: false }),
      style: new Style({
        stroke: new Stroke({
          color: 'blue',
          width: width + 2,
        }),
        fill: new Fill({
          color: 'rgba(0, 0, 255, 0.4)',
        }),
        image: new Circle({
          radius: width * 2,
          fill: new Fill({
            color: blue,
          }),
          stroke: new Stroke({
            color: white,
            width: width / 2,
          }),
        }),
        zIndex: Infinity,
      }),
    })

    const flightRouteArrowLayer = new VectorLayer({
      source: new VectorSource()
    })

    // const styles = {
    //   route: new Style({
    //     stroke: new Stroke({
    //       width: 6,
    //       color: [237, 212, 0, 0.8],
    //     }),
    //   }),
    // icon: new Style({
    //   image: new Icon({
    //     anchor: [0.5, 1],
    //     src: 'data/icon.png',
    //   }),
    // }),
    // geoMarker: new Style({
    //   image: new CircleStyle({
    //     radius: 7,
    //     fill: new Fill({ color: 'black' }),
    //     stroke: new Stroke({
    //       color: 'white',
    //       width: 2,
    //     }),
    //   }),
    // }),
    // }

    // const route = new Polyline({
    //   factor: 1e6,
    // }).readGeometry(polyline, {
    //   dataProjection: 'EPSG:4326',
    //   featureProjection: 'EPSG:3857',
    // })

    // const routeFeature = new Feature({
    //   type: 'route',
    //   geometry: route,
    // })
    // const startMarker = new Feature({
    //   type: 'icon',
    //   geometry: new Point(route.getFirstCoordinate()),
    // })
    // const endMarker = new Feature({
    //   type: 'icon',
    //   geometry: new Point(route.getLastCoordinate()),
    // })
    // const position = startMarker.getGeometry().clone()
    // const geoMarker = new Feature({
    //   type: 'geoMarker',
    //   geometry: position,
    // })

    // const vectorLayer = new VectorLayer({
    //   source: new VectorSource({
    //     features: [routeFeature, geoMarker, startMarker, endMarker],
    //   }),
    //   style: function (feature) {
    //     return styles[feature.get('type')]
    //   },
    // })

    // const pinFeat = new Feature({
    //   geometry: new Point(fromLonLat([128.31055618348088, 37.2565747455925])),
    //   style: new Style({
    //     image: new Icon({
    //       anchor: [0.5, 0.5],
    //       src: '/@/assets/icons/dock.png',
    //       scale: 0.2,
    //     })
    //   })
    // })

    // const markerLayer = new VectorLayer({
    //   source: new VectorSource({
    //     features: [pinFeat]
    //   }),
    //   style: function (feature) {
    //     return feature.get('style')
    //   }
    // })

    // const asiaCementContourLayer =
    //   new TileLayer({
    //     id: 'asiaCementContourLayer',
    //     visible: false,
    //     source:
    //     // raster,
    //     // new Raster({
    //       new XYZ({
    //         url: AMapConfig.url + '/tiles-raster/asia-cement/current/contours/{z}/{x}/{y}.png',
    //         tileSize: 256,
    //         transition: 0,
    //       }),
    //     // style: {
    //     //   variables: {
    //     //     exaggeration: 1.5,
    //     //     lightAltitude: 45,
    //     //     lightAzimuth: 315,
    //     //   },
    //     //   style: function (feature, resolution) {
    //     // const elev = getElevation(feature)
    //     // return getContourStyle(elev, elevMin, elevMax, resolution)
    //     // },
    //     // }),
    //     // operationType: 'image',
    //     // operation: shade,
    //     preload: 9, // Prefetch neighbor tiles for smooth panning
    //     maxZoom: 21,
    //     minZoom: 13,
    //     opacity: 0.3, // blend with basemap
    //     // projection: 'EPSG:3857'
    //     cacheSize: 256,
    //   })

    // === Load GeoJSON file (must be same-origin or CORS-enabled) ===
    // const contourLineSource30 = new VectorSource({
    //   url: AMapConfig.url + '/tiles-raster/asia-cement/current/contours_30_3857.geojson',
    //   format: new GeoJSON(),
    // })

    const contourLineSource10 = new VectorSource({
      url: AMapConfig.url + AMapConfig.path + '/contours_10_3857.geojson',
      format: new GeoJSON(),
    })

    const demSource = new GeoTIFF({
      sources: [{ url: AMapConfig.url + AMapConfig.path + '/contours_cog_3857.tif' }], // or remote URL
      normalize: false, // keep raw elevation values (meters)
    })

    const demLayer = new TileLayer({
      id: 'demLayer',
      source: demSource,
      opacity: 0.6,
      // style: { color: ['band', 1] }     // optional grayscale visualization
    })

    // const contourPolygonSource = new VectorSource({
    //   url: AMapConfig.url + '/tiles-raster/asia-cement/current/contours_simplified_polygon_3857.geojson',
    //   format: new GeoJSON(),
    // })

    // === Style contours ===
    const contourStyle = new Style({
      stroke: new Stroke({
        color: '#FF5733',
        width: 1.5
      }),
      text: new Text({
        font: '10px sans-serif',
        fill: new Stroke({ color: '#000' }),
        textBaseline: 'middle',
        textAlign: 'left',
      })
    })

    // === Define color scale function (blue → green → brown → white) ===
    function getColorForElevation (elev:number, min:number, max:number) {
      const ratio = (elev - min) / (max - min)
      const r = Math.min(255, Math.max(0, 255 * ratio)) // Red increases with elevation
      const g = Math.min(255, Math.max(0, 255 * (1 - ratio))) // Green decreases with elevation
      const b = Math.min(255, Math.max(0, 180 * (1 - ratio))) // Blue fades
      // const r = Math.min(255, Math.max(0, 255 * (1 - ratio))) // Red decreases with elevation
      // const g = Math.min(255, Math.max(0, 255 * ratio)) // Green increase
      // const b = Math.min(255, Math.max(0, 180 * (1 - ratio))) // Blue fades
      return `rgb(${r},${g},${b})`
    }

    const getElevation = (feature: any) => {
      const elev =
        feature?.get('elev') ??
        feature?.get('ELEVATION')

      if (elev !== undefined && !isNaN(elev)) {
        return Number(elev)
      }

      const coords = feature.getGeometry().getCoordinates()
      return coords !== undefined ? coords[0][2] : undefined
    }

    // === Find elevation range dynamically ===
    let elevMin = Infinity; let elevMax = -Infinity
    // contourLineSource30.on('addfeature', e => {
    //   const elev = getElevation(e.feature)
    //   if (elev !== undefined && !isNaN(elev)) {
    //     elevMin = Math.min(elevMin, elev)
    //     elevMax = Math.max(elevMax, elev)
    //   }
    // })
    contourLineSource10.on('addfeature', e => {
      const elev = getElevation(e.feature)
      if (elev !== undefined && !isNaN(elev)) {
        elevMin = Math.min(elevMin, elev)
        elevMax = Math.max(elevMax, elev)
      }
    })

    // === Style function ===
    // const contourStyleFn = (feature:any) => {
    //   const elev = getElevation(feature)
    //   const color = elev !== undefined
    //     ? getColorForElevation(elev, elevMin, elevMax)
    //     : '#888'
    //   return new Style({
    //     stroke: new Stroke({ color, width: 1.5 }),
    //     fill: new Fill({ color }),
    //     text: new Text({
    //       text: elev ? String(Math.round(elev)) : '',
    //       font: '10px sans-serif',
    //       // fill: new Stroke({ color: '#111' }),
    //       offsetY: -8,
    //     })
    //   })
    // }

    function getContourStyle (elev: number | undefined, elevMin: number, elevMax: number, resolution: number) {
      // Map zoom → font size (example formula)
      const baseSize = 10 // font size at zoom 0-ish
      const fontSize = Math.max(6, baseSize * Math.pow(2, -Math.log2(resolution))) // responsive

      const color = elev !== undefined
        ? getColorForElevation(elev, elevMin, elevMax)
        : '#888'

      // Label only every Nth contour
      // const showText = elev !== undefined && resolution < 1.3 && elev % 50 === 0
      const showText = resolution < 1.3

      return new Style({
        stroke: new Stroke({ color, width: 1.5 }),
        fill: new Fill({ color }),
        text: showText
          ? new Text({
            text: elev !== undefined ? String(Math.round(elev)) : '',
            font: `${fontSize}px sans-serif`,
            overflow: false,
            fill: new Fill({ color: '#111' }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
            offsetY: -8,
          })
          : undefined
      })
    }

    const contourLineLayer10 = new VectorLayer({
      id: 'asiaCementContourLineLayer10',
      source: contourLineSource10,
      visible: false,
      declutter: true,
      // style: contourStyleFn
      style: function (feature, resolution) {
        const elev = getElevation(feature)
        return getContourStyle(elev, elevMin, elevMax, resolution)
      },
      opacity: 0.6,
    })

    // const contourLineLayer30 = new VectorLayer({
    //   id: 'asiaCementContourLineLayer30',
    //   source: contourLineSource30,
    //   visible: true,
    //   declutter: true,
    //   // style: contourStyleFn
    //   style: function (feature, resolution) {
    //     const elev = getElevation(feature)
    //     return getContourStyle(elev, elevMin, elevMax, resolution)
    //   },
    //   opacity: 0.6,
    // })

    // const contourPolygonLayer = new VectorLayer({
    //   source: contourPolygonSource,
    //   style: contourStyleFn
    // })

    // const contourLayer = new VectorLayer({
    //   source: contourLineSource,
    //   style: feature => {
    //     // Label by elevation if available
    //     const elev = feature.get('elev')
    //     const style = contourStyle.clone()
    //     if (elev) style.getText().setText(String(elev))
    //     return style
    //   }
    // })
    const mapview = new View({
      center: fromLonLat([parseFloat(AMapConfig.longitude), parseFloat(AMapConfig.latitude)]),
      zoom: 13,
      projection: 'EPSG:3857',
      maxZoom: 24 // limit how far users can zoom in
    })

    const mapControl = defaultControls().extend([new GoogleLogoControl()])
    const map = new Map({
      target: container,
      layers: [
        waterLayer,
        landLayer,
        southKoreaLayer,
        demLayer,
        googleMapLayer,
        asiaCementBaseXyzLayer,
        asiaCementBaseLayer,
        // asiaCementContourLayer,
        // contourLineLayer30,
        contourLineLayer10,
        // contourPolygonLayer,
        flightRouteBoundaryLayer,
        flightRouteArrowLayer,
        // markerLayer
      ],
      controls: mapControl,
      view: mapview
    })

    // Create floating tooltip element
    const tooltip = { div: document.createElement('div'), expandWidth: 300 }
    tooltip.div.style.position = 'absolute'
    tooltip.div.style.background = 'rgba(0,0,0,0.75)'
    tooltip.div.style.color = 'white'
    tooltip.div.style.padding = '6px 10px'
    tooltip.div.style.borderRadius = '4px'
    tooltip.div.style.pointerEvents = 'none'
    tooltip.div.style.fontFamily = 'monospace'
    tooltip.div.style.fontSize = '13px'
    tooltip.div.style.whiteSpace = 'nowrap'
    tooltip.div.style.zIndex = '0'
    tooltip.div.style.display = 'none'
    document.body.appendChild(tooltip.div)

    // Update on pointer move pointermove (smooth) or 'click'/'singleclick' (less noisy)
    map.on('pointermove', evt => {
      if (evt.dragging) {
        tooltip.div.style.display = 'none'
        return
      }

      if (tooltip.div.style.zIndex === '0') {
        return
      }

      tooltip.div.style.display = 'block'

      if (app.config.globalProperties.$route.name === ERouterName.MAP_AREA) {
        app.config.globalProperties.$cursorTooltip.expandWidth = 25
      } else {
        app.config.globalProperties.$cursorTooltip.expandWidth = 300
      }

      // Position tooltip near cursor
      tooltip.div.style.left = `${evt.pixel[0] + tooltip.expandWidth}px`
      tooltip.div.style.top = `${evt.pixel[1] + 30}px`

      const data = demLayer.getData(evt.pixel)

      if (data && data.length > 0) {
        // For single-band elevation GeoTIFF → data[0] is the raw value (Float32)
        const elevation = data[0]

        const lonLat = toLonLat(evt.coordinate)

        tooltip.div.innerHTML = `
          Lon: ${lonLat[0].toFixed(9)}<br>
          Lat: ${lonLat[1].toFixed(9)}<br>
          X: ${evt.coordinate[0].toFixed(2)}<br>
          Y: ${evt.coordinate[1].toFixed(2)}<br>
          Elev: ${elevation.toFixed(2)} m
        `
      } else {
        tooltip.div.style.display = 'none'
      }
    })

    // Hide when mouse leaves map
    map.getViewport().addEventListener('mouseleave', () => {
      tooltip.div.style.display = 'none'
    })

    // Add draw interaction
    // const draw = new Draw({
    //   source: flightRouteBoundaryLayer.getSource() ?? undefined,
    //   type: 'Polygon',
    // })
    // mouseTool.setActive(false)

    setTimeout(() => {
      const view = map.getView()

      view.animate({
        zoom: 15, // target zoom level
        duration: 1000, // animation time in ms (1.5 seconds)
      })
    }, 1000) // 3000ms = 3 seconds

    app.config.globalProperties.$map = map
    // app.config.globalProperties.$mouseTool = draw
    app.config.globalProperties.$flightRouteArrowLayer = flightRouteArrowLayer
    app.config.globalProperties.$flightRouteBoundaryLayer = flightRouteBoundaryLayer
    app.config.globalProperties.$demLayer = demLayer
    app.config.globalProperties.$contourLineLayer10 = contourLineLayer10
    // app.config.globalProperties.$contourLineLayer30 = contourLineLayer30
    app.config.globalProperties.$cursorTooltip = tooltip

    const Cesium = await import('cesium')
    ;(window as any).Cesium = Cesium

    // then load olcs
    const { default: OLCesium } = await import('olcs')

    Cesium.Ion.defaultAccessToken = ''
    // Optional: Disable default credits / logo if they still appear
    Cesium.CreditDisplay.cesiumCredit = new Cesium.Credit(
      '<a href="https://cesium.com/" target="_blank"><img src="" style="vertical-align: -7px" title="Cesium"/></a>'
      , true)

    const TERRAIN_URL = AMapConfig.url + AMapConfig.path + '/terrain/'

    const ol3d = new OLCesium({ map: map, target: 'cesium-container' })
    const viewer = ol3d.getCesiumScene() as unknown as Cesium.Viewer

    try {
      viewer.terrainProvider = await Cesium.CesiumTerrainProvider.fromUrl(TERRAIN_URL)

      const imageryProvider = new Cesium.UrlTemplateImageryProvider({
        // url: AMapConfig.url + '/tiles-raster/asia-cement/current/tiles/{z}/{x}/{reverseY}.png',
        url: AMapConfig.url + AMapConfig.path + '/tiles/{z}/{x}/{y}.png',
        maximumLevel: 23,
        minimumLevel: 18,
        rectangle: Cesium.Rectangle.fromDegrees(
          AMapConfig.cesiumRectangleStartLongitude,
          AMapConfig.cesiumRectangleStartLatitude,
          AMapConfig.cesiumRectangleEndLongitude,
          AMapConfig.cesiumRectangleEndLatitude,
        )
      })
      viewer.imageryLayers.addImageryProvider(imageryProvider)
    } finally {
      app.config.globalProperties.$ol3d = ol3d
    }

    let googleCesium2DLayer:Cesium.Google2DImageryProvider | null = null
    try {
      googleCesium2DLayer = await Cesium.Google2DImageryProvider.fromUrl({
        key: googleMapKey,
        mapType: 'satellite', // "satellite", "roadmap", "terrain"
        // overlayLayerType: "layerRoadmap"  // Optional: for labels/roads overlay
      })
    } finally {
      app.config.globalProperties.$googleCesium2DLayer = googleCesium2DLayer
    }
    // map.addInteraction(mouseTool)
    // app.config.globalProperties.$aMap = OlMap
    // state.flightRouteBoundaryLayer = flightRouteBoundaryLayer
  }

  function globalPropertiesConfig (app:App) {
    initMap('g-container', app)
  }

  return {
    globalPropertiesConfig,
  }
}
